import {HeartIcon, HeartIconFilled} from '@/assets/icons/icons'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {HostPartialType} from '@/features/authentication/types'
import {useGetSavedHosts} from '@/features/guest/queries/useGetSavedHosts'
import {useSaveHost} from '@/features/guest/queries/useSaveHost'
import {useUnsaveHost} from '@/features/guest/queries/useUnsaveHost'
import {useGetConnectedUser} from '@/hooks/useGetConnectedUser'
import useRedirectToChatChannel from '@/hooks/useRedirectToChatChannel'
import {ProfileEventsPropsType} from '@/integrations/mixpanel/types'
import {useRootStore} from '@/store'
import {selectConnectModal} from '@/store/selectors'
import {StylePropsType} from '@/theme/style'
import {FC, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {StyledConnectNowButton, StyledRoot, StyledSaveButton, StyledSpinner} from './style'

//TODO added all types of buttons
type CtaBarProps = {
    ctaBarStyles?: StylePropsType
    host?: HostPartialType
    gradient?: boolean
    mixpanelData?: ProfileEventsPropsType
    handleSetEndTime?: () => void
}

export const CtaBar = ({ctaBarStyles, gradient = false, host, mixpanelData, handleSetEndTime}: CtaBarProps) => {
    const {mutate: saveHost, isPending: isPendingSave} = useSaveHost()
    const {mutate: unsaveHost, isPending: isPendingUnsave} = useUnsaveHost()
    const {data: savedHosts, isFetching} = useGetSavedHosts()
    const isSavedHost = !!savedHosts?.find(({id}) => id === host?.id)
    const [isSaved, setIsSaved] = useState(isSavedHost)
    const loading = isPendingSave || isPendingUnsave || isFetching

    useEffect(() => {
        setIsSaved(isSavedHost)
    }, [isSavedHost])

    const handleSaveButtonClick = () => {
        if (loading) return
        if (host?.id) {
            if (isSaved) {
                setIsSaved(false)
                unsaveHost(host?.id)
            } else {
                setIsSaved(true)
                saveHost(host?.id)
            }
        }
    }

    return (
        <StyledRoot ctaBarStyles={ctaBarStyles}>
            <StyledSaveButton onClick={() => handleSaveButtonClick()}>
                <StyledSpinner>{loading && <Spinner overlay={false} center={false} />}</StyledSpinner>
                {isSaved ? <HeartIconFilled /> : <HeartIcon />}
            </StyledSaveButton>
            {host && (
                <ConnectNowButton
                    hostId={host.id as number}
                    gradient={gradient}
                    mixpanelData={mixpanelData}
                    handleSetEndTime={handleSetEndTime}
                />
            )}
        </StyledRoot>
    )
}
CtaBar.displayName = 'CtaBar'

type ConnectNowButtonProps = {
    hostId: number
    gradient?: boolean
    mixpanelData?: ProfileEventsPropsType
    handleSetEndTime?: () => void
    className?: string
}
export const ConnectNowButton: FC<ConnectNowButtonProps> = ({
    hostId,
    gradient = false,
    mixpanelData,
    handleSetEndTime,
    className
}) => {
    const {t} = useTranslation()
    const {openModal} = useRootStore(selectConnectModal)
    const {connectedUser: connectedHost} = useGetConnectedUser(hostId)
    const redirectToChatChannel = useRedirectToChatChannel({receiverId: parseInt(`${hostId}`)})

    const handleConnectNow = () => {
        if (connectedHost) {
            redirectToChatChannel?.()
        } else {
            if (hostId) {
                openModal({mixpanelData, hostID: hostId})
                handleSetEndTime?.()
            }
        }
    }

    const renderCorrectCtaText = (() => {
        switch (true) {
            case !!connectedHost:
                return t('common:chat_now')
            default:
                return t('common:connect_now')
        }
    })()

    return (
        <>
            <StyledConnectNowButton className={className} gradient={gradient} onClick={handleConnectNow}>
                {renderCorrectCtaText}
            </StyledConnectNowButton>
        </>
    )
}
